import { useRouter } from '@application/utils';
import { ClassNames, useTheme } from '@emotion/react';
import { Accordion, Button, CustomIcon } from '@presentation/components';
import { BREAKPOINTS } from '@presentation/styles';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { FaBars } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';
import * as Styled from './styled';
import type { MtPuppyGuidePageProps } from './types';

export const MtPuppyGuidePage: FC<MtPuppyGuidePageProps> = (props) => {
  const { sections } = props;
  const theme = useTheme();
  const [activeId, setActiveId] = useState<string | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { translate } = useTranslation();
  const { routeLocationDescriptor } = useRouter();

  const isMobile = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.tablet}px)` });

  const nav = (
    <ClassNames>
      {({ css: classNameCss }) => {
        const wrapperClassname = classNameCss(Styled.accordionWrapperStyle());
        const labelClassname = classNameCss(Styled.accordionLabelStyle(theme));

        return (
          <Accordion
            wrapperClass={wrapperClassname}
            labelClass={labelClassname}
            noScroll
            tabs={sections.map((section) => ({
              title: section.title,
              icon: <CustomIcon name={section.icon} color={theme.color.blue.default} />,
              labelProps: {
                onClick: (event) => {
                  if (section.content?.length > 1) {
                    return;
                  }

                  event.preventDefault();
                  document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                },
                href: `#${section.id}`,
              },
              content:
                section.content?.length > 1 ? (
                  <>
                    {(section.content ?? [])
                      .filter((subSection) => !!subSection.title)
                      .map((subSection) => (
                        <Styled.SubLink key={subSection.id} active={activeId === subSection.id}>
                          <a
                            href={`#${subSection.id}`}
                            onClick={(event) => {
                              event.preventDefault();
                              setActiveId(subSection.id);
                              document
                                .getElementById(subSection.id)
                                ?.scrollIntoView({ behavior: 'smooth' });

                              inputRef.current?.click();
                            }}
                          >
                            {subSection.title}
                          </a>
                        </Styled.SubLink>
                      ))}
                  </>
                ) : undefined,
            }))}
          />
        );
      }}
    </ClassNames>
  );

  return (
    <Styled.Container>
      <Styled.DesktopNav>{nav}</Styled.DesktopNav>
      <Styled.Main ref={mainRef}>
        <Styled.TitleNavContainer>
          <Styled.TitleContainer>
            {isMobile && (
              <Styled.OpenButton
                htmlFor="mobile-puppy-nav"
                tabIndex={0}
                aria-haspopup="true"
                role="button"
                aria-controls="menu"
              >
                <FaBars />
              </Styled.OpenButton>
            )}
            <Styled.MainTitle>{translate('mt.puppyGuide.title')}</Styled.MainTitle>
          </Styled.TitleContainer>

          {isMobile && (
            <>
              <Styled.Input type="checkbox" id="mobile-puppy-nav" ref={inputRef} />
              <Styled.MobileNav data-puppy-nav>{nav}</Styled.MobileNav>
            </>
          )}
        </Styled.TitleNavContainer>

        <Styled.SectionsContainer>
          {sections.map((section) => (
            <Styled.Section key={section.id} id={section.id}>
              <Styled.SectionTitle>{section.title}</Styled.SectionTitle>

              {section.image && <Styled.Image src={section.image} alt="" />}

              {section.content.map((subSection) => (
                <Styled.SubSection key={subSection.id} id={subSection.id} className="subsection">
                  <Styled.SubSectionTitle>{subSection.title}</Styled.SubSectionTitle>
                  <Styled.SubSectionContent
                    dangerouslySetInnerHTML={{ __html: subSection.content }}
                  />

                  {subSection.cta && (
                    <Styled.SubSectionButton>
                      <Button
                        color="blue"
                        shrink
                        link={routeLocationDescriptor(subSection.cta.route)}
                      >
                        {subSection.cta?.text}
                      </Button>
                    </Styled.SubSectionButton>
                  )}
                </Styled.SubSection>
              ))}
            </Styled.Section>
          ))}
        </Styled.SectionsContainer>
      </Styled.Main>
    </Styled.Container>
  );
};
